import React, { useRef, useState } from "react";
import styles from "./AccordionFilter.module.scss";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";
import useScrollLock from "@/hooks/useScrollLock";

export type FilterCategory = {
  id: number;
  name: string;
};

interface AccordionFilterProps {
  categories: FilterCategory[];
  activeCategory: number | null;
  onCategoryChange: (categoryId: number | null) => void;
}

export const AccordionFilter = ({
  categories,
  activeCategory,
  onCategoryChange,
}: AccordionFilterProps) => {
  const { getLabel, getIcon } = useGlobalContext();
  const [isOpen, setIsOpen] = useState(false);
  const { toggleLock } = useScrollLock(false);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const activeCategoryName = activeCategory
    ? categories.find((c) => c.id === activeCategory)?.name
    : getLabel("accordion_filter_all_categories", "Alle Filterkategorien");

  const openModal = () => {
    setIsOpen(true);
    toggleLock(true);
    setTimeout(() => {
      modalContentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 0);
  };

  const closeModal = () => {
    setIsOpen(false);
    toggleLock(false);
  };

  const handleCategoryClick = (id: number | null) => {
    onCategoryChange(id);
    closeModal();
  };

  return (
    <div className={styles["accordion-filter"]}>
      {/* Mobile Trigger */}
      <button
        className={styles["mobile-trigger"]}
        onClick={openModal}
        type="button"
        aria-expanded={isOpen}
        aria-controls="filter-list"
        aria-haspopup="dialog"
      >
        <span className={styles["trigger-text"]}>{activeCategoryName}</span>
        <div className={styles["trigger-icon"]}>
          <CustomSVG
            src={getIcon("Filter")}
            width={24}
            height={24}
            wrapper="span"
            className="is-flex"
          />
        </div>
      </button>

      {/* Modal / Dropdown */}
      <div
        className={`${styles["filter-modal"]} ${isOpen ? styles["is-open"] : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-dialog-title"
        id="filter-list"
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className={styles["modal-backdrop"]} onClick={closeModal} />
        <div className={styles["modal-content"]} ref={modalContentRef}>
          <div className={styles["modal-header"]}>
            <span className={styles["modal-title"]} id="filter-dialog-title">
              {activeCategoryName}
            </span>
            <button
              className={styles["modal-close"]}
              onClick={closeModal}
              type="button"
              aria-label={getLabel("close", "Schließen")}
            >
              <CustomSVG
                src={getIcon("close_x_only")}
                width={16}
                height={16}
                wrapper="span"
                className="is-flex"
                style={{ fill: "currentColor" }}
              />
            </button>
          </div>
          <ul
            className={styles["modal-list"]}
            role="group"
            aria-label={getLabel("accordion_filter_label", "Filterkategorien")}
            data-lenis-prevent
          >
            <li>
              <button
                className={`p ${styles["filter-button"]} ${activeCategory === null ? styles["is-active"] : ""}`}
                onClick={() => handleCategoryClick(null)}
                type="button"
              >
                {getLabel(
                  "accordion_filter_all_categories",
                  "Alle Filterkategorien",
                )}
              </button>
            </li>
            {categories.map((category) => (
              <li key={category.id}>
                <button
                  className={`p ${styles["filter-button"]} ${activeCategory === category.id ? styles["is-active"] : ""}`}
                  onClick={() => handleCategoryClick(category.id)}
                  type="button"
                >
                  {category.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Desktop List */}
      <div
        className={styles["desktop-list"]}
        role="group"
        aria-label={getLabel("accordion_filter_label", "Filterkategorien")}
      >
        <button
          className={`p ${styles["filter-button"]} ${activeCategory === null ? styles["is-active"] : ""}`}
          onClick={() => onCategoryChange(null)}
          type="button"
        >
          {getLabel("accordion_filter_all_categories", "Alle Filterkategorien")}
        </button>
        {categories.map((category) => (
          <button
            key={category.id}
            className={`p ${styles["filter-button"]} ${activeCategory === category.id ? styles["is-active"] : ""}`}
            onClick={() => onCategoryChange(category.id)}
            type="button"
          >
            {category.name}
          </button>
        ))}
      </div>
    </div>
  );
};
